import { getAttributeOrUndefined, PUB_SUB_EVENTS } from '@/scripts/core/global'
import { createVariantChangeEvent, publish } from '@/scripts/core/global'
import {
	getAttributeOrThrow,
	qsaOptional,
	qsaRequired,
	qsOptional,
	qsRequired,
} from '@/scripts/core/global'
import { type ProductForm } from '@/scripts/product/product-form'
import { type PickupAvailability } from '@/scripts/optional/pickup-availability'
import { UcoastEl } from '@/scripts/core/UcoastEl'
import { type ProductVariant } from '@/scripts/shopify'

export class VariantSelects extends UcoastEl {
	static htmlSelector = 'variant-selects'
	static selectors = {}
	instanceSelectors = VariantSelects.selectors
	currentVariant?: ProductVariant
	options?: string[]
	variantData?: ProductVariant[]
	productId: number
	ctaTextMod?: string
	constructor() {
		super()
		this.setInstanceSelectors()
		this.addEventListener('change', this.onVariantChange)
		this.productId = parseInt(getAttributeOrThrow('data-product-id', this))
		this.ctaTextMod = getAttributeOrUndefined('data-cta-text-mod', this)
	}

	setInstanceSelectors() {
		this.instanceSelectors = VariantSelects.selectors
	}

	onVariantChange(source?: Event | 'external_event') {
		const fromExternal = source === 'external_event'
		this.updateOptions()
		this.updateMasterId()
		this.toggleAddButton(true, '', false)
		this.updatePickupAvailability()
		this.removeErrorMessage()
		this.updateVariantStatuses()

		if (!this.currentVariant) {
			this.toggleAddButton(true, '', true)
			this.setUnavailable()
		} else {
			//this.updateMedia()
			this.updateURL()
			this.updateVariantInput()
			this.renderProductInfo(fromExternal)
		}
	}

	updateOptionsFromExternal(externalVariant: ProductVariant) {
		qsaRequired<HTMLSelectElement>('select', this).forEach(
			(select, index) => {
				select.value = externalVariant.options[index]
			}
		)
		this.onVariantChange('external_event')
	}

	updateOptions() {
		this.options = Array.from(
			qsaRequired<HTMLSelectElement>('select', this),
			(select) => select.value
		)
	}

	updateMasterId() {
		this.currentVariant = this.getVariantData().find((variant) => {
			return !variant.options
				.map((option, index) => {
					return this.options && this.options[index] === option
				})
				.includes(false)
		})
	}

	updateURL() {
		if (!this.currentVariant || this.dataset.updateUrl === 'false') return
		if (!window.location.href.includes('/products/')) return
		window.history.replaceState(
			{},
			'',
			`${this.dataset.url}?variant=${this.currentVariant.id}`
		)
	}

	updateVariantInput() {
		const currentVariant = this.currentVariant
		if (!currentVariant)
			throw new Error('cannot updateVariantInput without currentVariant')
		let productFormSelectors = `#product-form-${this.getSectionDomID()}, #product-form-installment-${this.getSectionDomID()}`
		if (this.getSectionDomID().includes('dynamic-product-form'))  {
			productFormSelectors = `#${this.getSectionDomID()}_product-form`
		}
		const productForms = qsaOptional(productFormSelectors)
		productForms?.forEach((productForm) => {
			const input = qsRequired<HTMLInputElement>(
				'input[name="id"]',
				productForm
			)
			input.value = `${currentVariant.id}`
			input.dispatchEvent(new Event('change', { bubbles: true }))
		})
	}

	getVariantOptionFromIndex(variant: ProductVariant, index: number) {
		switch (index) {
			case 1:
				return variant.option1
			case 2:
				return variant.option2
			case 3:
				return variant.option3
			default:
				throw new Error('invalid index')
		}
	}

	updateVariantStatuses() {
		if (!this.variantData)
			throw new Error('cannot updateVariantStatuses without variantData')
		const selectedOptionOneVariants = this.variantData.filter(
			(variant) =>
				qsRequired<HTMLInputElement | HTMLOptionElement>(
					':checked',
					this
				).value === variant.option1
		)
		const inputWrapperNodes = qsaOptional('.product-form__input', this)
		const inputWrappers = inputWrapperNodes?.length
			? Array.from(inputWrapperNodes)
			: []
		if (inputWrappers.length) {
			inputWrappers.forEach((option: HTMLElement, index: number) => {
				if (index === 0) return
				const optionInputNodes = qsaOptional<
					HTMLInputElement | HTMLOptionElement
				>('input[type="radio"], option', option)
				const optionInputs = optionInputNodes?.length
					? Array.from(optionInputNodes)
					: []

				const previousOptionSelected = qsRequired<
					HTMLInputElement | HTMLOptionElement
				>(':checked', inputWrappers[index - 1]).value
				const availableOptionInputsValue = selectedOptionOneVariants
					.filter(
						(variant) =>
							variant.available &&
							this.getVariantOptionFromIndex(variant, index) ===
								previousOptionSelected
					)
					.map((variantOption) =>
						this.getVariantOptionFromIndex(variantOption, index + 1)
					)
				this.setInputAvailability(
					optionInputs,
					availableOptionInputsValue
				)
			})
		} else {
			console.warn('no input wrappers found')
		}
	}

	setInputAvailability(
		listOfOptions: (HTMLInputElement | HTMLOptionElement)[],
		listOfAvailableOptions: (string | null)[]
	) {
		listOfOptions.forEach((input) => {
			if (listOfAvailableOptions.includes(input.getAttribute('value'))) {
				input.innerText = getAttributeOrThrow('value', input)
			} else {
				input.innerText =
					window.variantStrings.unavailable_with_option.replace(
						'[value]',
						getAttributeOrThrow('value', input)
					)
			}
		})
	}

	updatePickupAvailability() {
		const pickUpAvailability = qsOptional<PickupAvailability>(
			'pickup-availability'
		)
		if (!pickUpAvailability) return
		const currentVariant = this.currentVariant

		if (currentVariant && currentVariant.available) {
			pickUpAvailability.fetchAvailability(`${currentVariant.id}`)
		} else {
			pickUpAvailability.removeAttribute('available')
			pickUpAvailability.innerHTML = ''
		}
	}

	removeErrorMessage() {
		const section = this.closest('section')
		if (!section) return

		const productForm = qsOptional<ProductForm>('product-form', section)
		if (productForm) productForm.handleErrorMessage()
	}

	getSectionApiId() {
		if (this.dataset.originalSection) {
			return this.dataset.originalSection
		}
		return getAttributeOrThrow('data-section', this)
	}
	getSectionDomID() {
		if (this.dataset.sectionDomId) {
			return getAttributeOrThrow('data-section-dom-id', this)
		}
		if (this.dataset.originalSection) {
			return this.dataset.originalSection
		}
		return getAttributeOrThrow('data-section', this)
	}

	renderProductInfo(fromExternal?: boolean) {
		if (!this.currentVariant)
			throw new Error(
				'this.currentVariant not selected, renderProductInfo called too early'
			)
		const requestedVariantId = this.currentVariant.id
		let sectionId = this.getSectionApiId()
		let query = `${this.dataset.url}?variant=${requestedVariantId}&section_id=${sectionId}`
		if (this.getAttribute('data-location') === 'product-sticky-cta' && this.hasAttribute('data-section-override'))  {
			// evil hack but tired of debugging this.
			const overrideSectionId = getAttributeOrThrow('data-section-override', this)
			query = `${this.dataset.url}?variant=${requestedVariantId}&section_id=${overrideSectionId}`
		}

		fetch(query)
			.then((response) => response.text())
			.then((responseText) => {
				if (!this.currentVariant)
					throw new Error(
						'this.currentVariant not selected, renderProductInfo called too early'
					)
				// prevent unnecessary ui changes from abandoned selections
				if (this.currentVariant.id !== requestedVariantId) return
				const sectionDestinationId = this.getSectionDomID()

				const html = new DOMParser().parseFromString(
					responseText,
					'text/html'
				)
				const destination = document.getElementById(
					`price-${sectionDestinationId}`
				)
				const source = html.getElementById(`price-${sectionId}`)



				const skuSource = html.getElementById(`Sku-${sectionId}`)
				const skuDestination = document.getElementById(
					`Sku-${sectionDestinationId}`
				)
				const inventorySource = qsOptional(
					`#Inventory-${sectionId}`,
					html
				)
				const inventoryDestination = document.getElementById(
					`Inventory-${sectionDestinationId}`
				)

				const afterpaySource = qsOptional('[data-afterpay-price]', html)
				const afterpayDestination = qsOptional('[data-afterpay-price]', document)

				if (source && destination)
					destination.innerHTML = source.innerHTML
				if (inventorySource && inventoryDestination)
					inventoryDestination.innerHTML = inventorySource.innerHTML
				if (skuSource && skuDestination) {
					skuDestination.innerHTML = skuSource.innerHTML
					skuDestination.classList.toggle(
						'visibility-hidden',
						skuSource.classList.contains('visibility-hidden')
					)
				}

				if (afterpaySource && afterpayDestination) {
					afterpayDestination.innerHTML = afterpaySource.innerHTML
				}

				const price = document.getElementById(
					`price-${sectionDestinationId}`
				)

				if (price) price.classList.remove('visibility-hidden')

				if (inventoryDestination)
					inventoryDestination.classList.toggle(
						'visibility-hidden',
						inventorySource?.innerText === ''
					)

				const addButtonUpdated = html.getElementById(
					`ProductSubmitButton-${sectionId}`
				)
				this.toggleAddButton(
					addButtonUpdated
						? addButtonUpdated.hasAttribute('disabled')
						: true,
					window.variantStrings.soldOut
				)

				const preorderSource = qsOptional('[data-dynamic-preorder]', html)
				const preorderDestination = qsOptional('[data-dynamic-preorder]')
				if (preorderSource && preorderDestination) {
					preorderDestination.innerHTML = preorderSource.innerHTML
				}

				const ctaSource = html.getElementById(
					`ProductSubmitButton-${sectionId}`
				)
				const ctaDestination = document.getElementById(
					`ProductSubmitButton-${sectionDestinationId}`
				)


				if (ctaDestination && this.ctaTextMod && qsOptional('[data-uc-cta-span]', ctaDestination) && this.currentVariant.inventory_status === 'InStock') {
					qsRequired('[data-uc-cta-span]', ctaDestination).innerHTML = this.ctaTextMod
				}
				else if (ctaSource && ctaDestination) {
					ctaDestination.innerHTML = ctaSource.innerHTML
					const sourceDataOosPopupTrigger = getAttributeOrUndefined(
						'data-oos-popup-trigger',
						ctaSource
					)
					if (sourceDataOosPopupTrigger) {
						ctaDestination.setAttribute(
							'data-oos-popup-trigger',
							sourceDataOosPopupTrigger
						)
					} else {
						ctaDestination.removeAttribute('data-oos-popup-trigger')
					}
				}

				publish(
					PUB_SUB_EVENTS.variantChange,
					createVariantChangeEvent({
						source: 'product-form',
						data: {
							sectionId,
							html,
							variant: this.currentVariant,
							productId: this.productId,
							fromExternal: fromExternal ?? false,
						},
					})
				)
			})
	}

	toggleAddButton(disable = true, text: string, modifyClass = true) {
		const productForm = qsOptional<HTMLFormElement>(
			`#product-form-${this.getSectionDomID()}`
		)
		if (!productForm) return
		const addButton = qsOptional('[name="add"]', productForm)
		const addButtonText = qsOptional('[name="add"] > span', productForm)
		if (!addButton || !addButtonText) return

		if (disable) {
			addButton.setAttribute('disabled', 'disabled')
			if (text) addButtonText.textContent = text
		} else {
			addButton.removeAttribute('disabled')
			addButtonText.textContent = window.variantStrings.addToCart
		}

		if (!modifyClass) return
	}

	setUnavailable() {
		const button = qsRequired(`#product-form-${this.getSectionDomID()}`)
		const addButton = qsOptional('[name="add"]', button)
		const addButtonText = qsOptional('[name="add"] > span', button)
		const price = document.getElementById(`price-${this.getSectionDomID()}`)
		const inventory = document.getElementById(
			`Inventory-${this.getSectionDomID()}`
		)
		const sku = document.getElementById(`Sku-${this.getSectionDomID()}`)

		if (!addButton || !addButtonText) return
		addButtonText.textContent = window.variantStrings.unavailable
		if (price) price.classList.add('visibility-hidden')
		if (inventory) inventory.classList.add('visibility-hidden')
		if (sku) sku.classList.add('visibility-hidden')
	}

	getVariantData(): ProductVariant[] {
		const textContent = qsRequired<HTMLScriptElement>(
			'[type="application/json"]',
			this
		).textContent
		if (!textContent) throw new Error('Variant data not found')
		const updatedVariantData = this.variantData ?? JSON.parse(textContent)
		this.variantData = updatedVariantData
		return updatedVariantData
	}
}
